import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Drop-in replacement for `useState(defaultValue)` that backs the value with
 * a URL query parameter. Lets filter pages survive a navigate-and-back cycle
 * without writing reducers everywhere.
 *
 *   const [tier, setTier] = useUrlState("tier", "all");
 *
 * The setter accepts either a value or a (prev) => next function, like
 * `useState`. When `value === defaultValue` the param is dropped from the URL
 * so we don't litter it with `?tier=all&risk=all`.
 *
 * Pages that use this combine cleanly with `firmFromQS()` — when the user
 * jumps to a firm 360, the full pathname+search is passed as `from`, so the
 * Back button restores the exact filter state.
 */
export function useUrlState(name, defaultValue = "") {
  const [params, setParams] = useSearchParams();
  const raw = params.get(name);
  const value = raw === null ? defaultValue : raw;

  const setValue = useCallback(
    (next) => {
      // Functional updater support — mirrors React.useState's API.
      const resolved = typeof next === "function" ? next(value) : next;
      const np = new URLSearchParams(params);
      if (resolved === defaultValue || resolved === "" || resolved == null) {
        np.delete(name);
      } else {
        np.set(name, String(resolved));
      }
      setParams(np, { replace: true });
    },
    [name, defaultValue, params, setParams, value]
  );

  return [value, setValue];
}
